/**
 * Shrink an image in the browser before it is uploaded.
 *
 * Cloudinary used to do this on delivery — every URL carried `f_auto,q_auto`,
 * so it re-encoded to WebP and picked a quality per request. R2 is plain
 * object storage and serves back exactly the bytes it was given, so the
 * shrinking has to happen before the upload instead of after.
 *
 * Doing it here rather than on the server keeps the API container out of the
 * image-processing business (no native `sharp` dependency in the Docker
 * image) and means the phone or laptop uploads far fewer bytes in the first
 * place. The server still enforces a hard size ceiling — this is the thing
 * that keeps files small, not the thing that keeps them safe.
 */

/** Longest edge, in pixels. A portal background is displayed full-screen on a
    phone; beyond this the extra detail is invisible and just costs bytes. */
const MAX_EDGE = 1920;

/** WebP quality. 0.82 is the point where artefacts stop being visible on
    photographic backgrounds while the file stays several times smaller. */
const QUALITY = 0.82;

/**
 * Formats we deliberately do NOT touch:
 *  - GIF: drawing it to a canvas keeps only the first frame, silently killing
 *    the animation. An operator uploading an animated banner would get a
 *    still image back with no warning.
 *  - SVG: already tiny, vector, and rasterising it defeats the point.
 */
const SKIP_TYPES = new Set(['image/gif', 'image/svg+xml']);

export interface CompressResult {
   file: File;
   /** False when the original was returned untouched, for any reason. */
   compressed: boolean;
}

/**
 * Returns a WebP copy scaled to fit MAX_EDGE, or the original file when
 * compression is skipped or would not help.
 *
 * Never throws: a browser that lacks canvas WebP encoding, a corrupt file, or
 * any other failure falls back to uploading the original. A slightly larger
 * upload is a far better outcome than a blocked one.
 */
export async function compressImage(
   file: File,
   /** Community photos use a smaller edge (1600) and a ~480px thumbnail; defaults unchanged. */
   opts: { maxEdge?: number; quality?: number; suffix?: string } = {}
): Promise<CompressResult> {
   const maxEdge = opts.maxEdge ?? MAX_EDGE;
   const quality = opts.quality ?? QUALITY;
   if (!file.type.startsWith('image/') || SKIP_TYPES.has(file.type)) {
      return { file, compressed: false };
   }

   try {
      const bitmap = await createImageBitmap(file);
      const { width, height } = bitmap;
      const scale = Math.min(1, maxEdge / Math.max(width, height));
      const targetW = Math.max(1, Math.round(width * scale));
      const targetH = Math.max(1, Math.round(height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
         bitmap.close?.();
         return { file, compressed: false };
      }
      ctx.drawImage(bitmap, 0, 0, targetW, targetH);
      bitmap.close?.();

      const blob = await new Promise<Blob | null>((resolve) => {
         canvas.toBlob((b) => resolve(b), 'image/webp', quality);
      });

      // toBlob hands back null when the browser cannot encode WebP, and some
      // browsers quietly fall back to PNG — which is routinely BIGGER than the
      // JPEG that came in. Only accept a genuine saving.
      if (!blob || blob.type !== 'image/webp' || blob.size >= file.size) {
         return { file, compressed: false };
      }

      const name = file.name.replace(/\.[a-z0-9]+$/i, '') || 'image';
      return {
         file: new File([blob], `${name}${opts.suffix ?? ''}.webp`, {
            type: 'image/webp',
            lastModified: file.lastModified,
         }),
         compressed: true,
      };
   } catch {
      return { file, compressed: false };
   }
}
